/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { debounce, isEqual } from 'lodash';

import { isAuthenticatedUser } from 'js/lib/user';

import { Autocomplete, Button, IconButton, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { AIGenerateBrandedIcon } from '@coursera/cds-icons';

import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import { useIsComposablePageHeader } from 'bundles/page-header/contexts/PageHeaderContext';
import AutocompleteItemIcon from 'bundles/search-common/components/searchbar/AutocompleteItemIcon';
import AutocompleteOnboardingOption from 'bundles/search-common/components/searchbar/AutocompleteOnboardingOption';
import AutocompleteOptionTrackingDiv from 'bundles/search-common/components/searchbar/AutocompleteOptionTrackingDiv';
import AutocompleteRecentlySearchedOption from 'bundles/search-common/components/searchbar/AutocompleteRecentlySearchedOption';
import AutocompleteSearchProductCard from 'bundles/search-common/components/searchbar/AutocompleteSearchProductCard';
import AutocompleteTrendingOption from 'bundles/search-common/components/searchbar/AutocompleteTrendingOption';
import { SEARCH_MAIN_INDEX_ID, SEARCH_SUGGESTIONS_INDEX_ID } from 'bundles/search-common/constants';
import {
  AUTOCOMPLETE_MODES,
  AUTOCOMPLETE_SECTION_PREFIXES,
  type AutocompleteMode,
  DEBOUNCE_DURATION_MS,
  LEARNER_FAVORITES_GROUP_ID,
  ONBOARDING_GROUP_ID,
  RECENTLY_VIEWED_GROUP_ID,
  RECENT_SEARCHES_GROUP_ID,
  TRENDING_SEARCHES_GROUP_ID,
  TRENDING_SEARCH_RESULTS_CONFIG,
  TRENDING_SEARCH_SUGGESTIONS_CONFIG,
} from 'bundles/search-common/constants/autocomplete';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';
import { clearAllRecentSearches } from 'bundles/search-common/utils/SearchUtils';
import {
  type AutocompleteItemGroup,
  type AutocompleteItems,
  createAsYouTypeAutocompleteItems,
  createZeroStateAutocompleteItems,
} from 'bundles/search-common/utils/autocomplete';
import { getShowNewAutocomplete } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/search-common';

type WrapperProps = {
  hideMobileSearchPage?: () => void;
  recentlySearchedItems?: AutocompleteItems;
  recentlyViewedItems?: AutocompleteItems;
  onRefreshItems?: () => void;
};

type Props = WrapperProps & {
  searchIsOpen: boolean;
  searchText: string;
  onInputChange: (value: string) => void;
  autocompleteMode: AutocompleteMode;
  onSubmit: (query: string, mergedItemsForTracking?: AutocompleteItems) => void;
};

const styles = {
  backdrop: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--cds-color-utility-overlay);
    z-index: 1000;

    /* Hide on mobile/tablet - only show on desktop */
    @media (max-width: ${breakpoints.values.md - 1}px) {
      display: none;
    }
  `,
  autocomplete: (
    isComposablePageHeader?: boolean,
    trendingTitle?: string,
    isConsumerSearchPage?: boolean,
    isEqp?: boolean,
    hasDegreeButton?: boolean,
    showNewAutocomplete?: boolean
  ) => css`
    /* Ensure entire autocomplete component is above backdrop */
    position: relative;
    z-index: 1001;

    .cds-popoverDropdown-paper {
      /* if we don't remove 2px from CDS, we have an awkward gap with page content */
      margin-top: var(--cds-spacing-100);
      padding: var(--cds-spacing-200);

      /* Override any max-width constraints from CDS */
      max-width: none !important;

      /* Ensure consistent positioning regardless of input content */
      position: absolute !important;
      transform-origin: top left !important;

      /* Ensure dropdown appears above backdrop */
      z-index: 1001 !important;

      /* Responsive dropdown widths based on breakpoints */
      @media (min-width: ${breakpoints.values.sm}px) and (max-width: 1280px) {
        width: 548px !important;
      }

      /* For 1024-1060px when not consumer search and not EQP - make popover viewport width (override above)
       * Note: 1060px is mobile header breakpoint. This range (tablet landscape/small laptop) needs max space.
       * Mobile header search bar covers full width but has padding. Dropdown width = 100vw - (padding * 2).
       * Consumer search & EQP have their own handling, so excluded here. */
      ${!isConsumerSearchPage &&
      !isEqp &&
      css`
        @media (min-width: 1024px) and (max-width: 1060px) {
          width: calc(100vw - var(--cds-spacing-100) * 2) !important;
          min-width: calc(100vw - var(--cds-spacing-100) * 2) !important;
        }
      `}

      @media (min-width: 1281px) and (max-width: 1439px) {
        width: 802px !important;
      }

      @media (min-width: 1440px) {
        width: 1056px !important;
      }

      /* Reduce width when degrees link is present in header */
      ${hasDegreeButton &&
      css`
        @media (min-width: 1281px) and (max-width: 1439px) {
          width: 658px !important;
        }

        @media (min-width: 1440px) {
          width: 912px !important;
        }
      `}
    }

    /* Add padding to listbox on mobile/tablet */
    @media (max-width: ${breakpoints.values.md - 1}px) {
      [role='listbox'][aria-label='Suggestions'] {
        padding: var(--cds-spacing-300) var(--cds-spacing-200) 0 var(--cds-spacing-200) !important;
      }
    }

    .cds-multiSelectGroup-label {
      padding: 0 !important;
      margin: 0 0 var(--cds-spacing-150) 0 !important;
    }

    /* Base styling for multiSelectGroup-list - remove padding from MuiButtonBase-root */
    .cds-multiSelectGroup-list {
      gap: var(--cds-spacing-150) !important;

      > div[role='option'] {
        &.MuiButtonBase-root,
        &[class*='MuiButtonBase-root'] {
          padding: 0 !important;
        }

        /* Show X button when option has CDS focused class */
        &.cds-AutocompleteOption-focused {
          .clear-icon-button {
            opacity: 1 !important;
          }
        }
      }
    }

    /* Also target MuiButtonBase-root directly within multiSelectGroup-list with higher specificity */
    .cds-multiSelectGroup-list div[role='option'][class*='MuiButtonBase-root'] {
      padding: 0 !important;
    }

    /* Remove overflow and spacing between prefix and content */
    .cds-AutocompleteOption-prefix {
      overflow: visible !important;
      margin-right: 0 !important;
      padding-right: 0 !important;
    }

    .cds-AutocompleteOption-content {
      overflow: visible !important;
      margin-left: 0 !important;
      padding-left: 0 !important;
    }

    /* Remove any gap between prefix and content */
    .MuiButtonBase-root {
      .cds-AutocompleteOption-prefix + .cds-AutocompleteOption-content {
        margin-left: 0 !important;
        padding-left: 0 !important;
      }
    }

    /* Style for Trending Topics Group */
    ${trendingTitle &&
    css`
      div[role='group'][aria-label='${trendingTitle}'].cds-multiSelectGroup-list {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--cds-spacing-100) !important;

        > div[role='option'] {
          padding: 0 !important;
          background-color: transparent !important;
          display: inline-block !important;
          width: auto !important;
          min-width: auto !important;
        }

        button.cds-button-secondary,
        .cds-button-secondary {
          border: 1px solid var(--cds-color-grey-100) !important;
          color: var(--cds-color-grey-975) !important;
          box-shadow: none !important;
        }
      }
    `}

    /* Style for Recently Viewed / Learner Favorites Group */
    div[role='group'][aria-label*='Recently viewed'].cds-multiSelectGroup-list,
    div[role='group'][aria-label*='learner favorites'].cds-multiSelectGroup-list {
      display: flex;
      flex-direction: row;
      align-items: stretch; /* Make all children stretch to the same height */
      gap: var(--cds-spacing-100);

      > div[role='option'] {
        padding: 0 !important;
        background-color: transparent !important;
        display: flex !important;
        flex-direction: column;
        align-self: stretch; /* Ensure this element stretches to match parent height */

        /* Stretch MuiButtonBase-root - target the class directly */
        &.MuiButtonBase-root {
          align-self: stretch !important;
          display: flex !important;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0; /* Allow flex children to shrink below content size */
        }

        .cds-AutocompleteOption-content {
          > div {
            flex: 1;
          }
        }

        /* Mobile (< sm, < 600px): width 128px */
        @media (max-width: ${breakpoints.values.md - 1}px) {
          > div {
            width: 230px !important;
            max-width: 230px !important;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }

      /* Tablet and desktop (>= sm, >= 600px): width 246px */
      @media (min-width: ${breakpoints.values.md}px) {
        > div[role='option'] {
          width: 240px !important;
          min-width: 240px !important;
          max-width: 240px !important;

          > div {
            width: 240px !important;
            max-width: 240px !important;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }

      /* Mobile (< sm, < 600px): Make the section independently scrollable */
      @media (max-width: ${breakpoints.values.md - 1}px) {
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */

        /* Prevent cards from shrinking */
        > div[role='option'] {
          flex-shrink: 0 !important;
        }

        /* Hide scrollbar for webkit browsers (Chrome, Safari, etc.) */
        &::-webkit-scrollbar {
          display: none;
        }
      }
    }

    /* Style for Autocomplete Group Labels */
    .cds-multiSelectGroup-label {
      ${typography2.subtitleMedium}
    }

    .clear-all-recent-searches-button {
      ${typography2.actionSecondary}
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      margin-left: auto;
      padding-left: var(--cds-spacing-200);
      color: var(--cds-color-neutral-primary-weak) !important;

      &:hover {
        text-decoration: underline;
        background: none !important;
      }

      &:focus {
        outline: 2px solid var(--cds-color-blue-800);
        outline-offset: 2px;
        border-radius: var(--cds-border-radius-100);
      }
    }

    /* Visual separators - show space but hide the line */
    div[data-testid='visual-separator'],
    .cds-multiSelectGroup-divider {
      height: 20px;

      hr,
      .MuiDivider-root {
        background: none !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }

    /* temporarily hidden for the sake of experiment */

    .cds-SearchClearButton-searchClearButton,
    .cds-Search-clearPlaceholder {
      display: none;
    }

    &.cds-Search-base {
      margin: auto;
      margin-left: 4px;
      margin-top: ${isComposablePageHeader ? '0' : '8px'};
      height: 44px;
      width: 100%;
      max-width: ${isComposablePageHeader ? '480px' : 'none'};

      /* For 1024-1060px when not consumer search and not EQP - make search bar full width minus padding */
      ${!isConsumerSearchPage &&
      !isEqp &&
      css`
        @media (min-width: 1024px) and (max-width: 1060px) {
          width: calc(100vw - var(--cds-spacing-100) * 2) !important;
          max-width: calc(100vw - var(--cds-spacing-100) * 2) !important;
          margin: var(--cds-spacing-100) !important;
        }
      `}

      @media (max-width: 1023px) {
        /* technically we show mobile header */
        ${!isEqp && !isConsumerSearchPage && isComposablePageHeader && showNewAutocomplete
          ? css`
              width: 100%;
              max-width: 100%;
              padding-left: var(--cds-spacing-100);
              padding-right: var(--cds-spacing-100);
              margin-top: var(--cds-spacing-50);
            `
          : css`
              width: ${isComposablePageHeader ? '100px' : '100%'};
              max-width: ${isComposablePageHeader ? '100px' : 'none'};
            `}
      }

      > div {
        border-radius: 24px;
        border: 1px solid var(--cds-color-grey-50);

        fieldset {
          /* remove duplicate border */
          border: none;
        }

        ::before {
          /* a11y border */
          border-radius: 24px !important;
        }
      }

      .cds-Search-prefix {
        display: none;
      }

      .cds-Search-suffix {
        /* for search button right spacing */
        padding-right: 4px;
      }

      /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
      input {
        margin-bottom: 0;
        border: 0;
        box-shadow: none;
        background: transparent;
      }

      /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
      input[aria-invalid='true']:invalid:focus {
        box-shadow: none;
      }
    }

    & .cds-AutocompleteOption-suffix {
      display: none !important;
    }

    & .cds-AutocompleteOption-checked:not(:hover) {
      background: transparent !important;
    }
  `,
  searchButton: css`
    /* circular shape of search button */
    border-radius: 24px;
  `,
  hackyRetroButtonStyle: css`
    border-radius: 0;
  `,

  hackyRetroInputStyle: css`
    &.cds-Search-base {
      margin-top: 12px;
      margin-bottom: 12px;
      margin-left: 0;
      width: 100%;
      height: 40px;

      > div::before {
        border-radius: 0 !important;
      }

      input.cds-input-input {
        padding-left: 8px !important;
      }
    }

    .cds-Search-inputRoot {
      /* flex-direction: row-reverse; */
      border-radius: 0 !important;
      border: 1px solid var(--cds-color-grey-600) !important;
    }

    .cds-Search-prefix {
      padding: 0;
      margin: 0;
      align-items: start;
      position: relative;
      top: -1px;
      left: -2px;

      .cds-iconButton-medium {
        height: 40px;
        width: 38px;
        background-color: var(--cds-color-blue-800);
      }
    }
  `,
};

const SearchBarContent = (props: Props) => {
  const {
    searchIsOpen,
    hideMobileSearchPage,
    searchText,
    onSubmit,
    autocompleteMode,
    onInputChange,
    recentlySearchedItems = [],
    recentlyViewedItems = [],
    onRefreshItems,
  } = props;

  const isComposablePageHeader = useIsComposablePageHeader();
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'));

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(!!searchIsOpen);
  const [hasDegreeButton, setHasDegreeButton] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const isRemovingItemRef = React.useRef(false);
  const isConsumerSearchPage = useIsConsumerSerp();
  const isEqp = useIsEqp();
  const rootInputRef = React.useRef<HTMLInputElement>(null);
  const isProgrammaticOpenRef = React.useRef(false);
  const previousSearchTextRef = React.useRef<string>(searchText);
  const isUserLoggedIn = isAuthenticatedUser();
  const location = typeof window !== 'undefined' ? window.location : null;
  const referrer = location?.search ? `${location.pathname}${location.search}` : location?.pathname || '';
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });
  // a separate, durable query results set for the "trending" section of the autocomplete
  const { results: trendingSuggestions, error: trendingItemsError } = useSearchQuery(
    TRENDING_SEARCH_SUGGESTIONS_CONFIG,
    {
      skip: inServerContext,
    }
  );
  const { results: trendingProducts, error: trendingProductsError } = useSearchQuery(TRENDING_SEARCH_RESULTS_CONFIG, {
    skip: inServerContext || recentlyViewedItems.length >= 4,
  });
  // user input derived search results
  const { getSearchResults, updateQuery, isLoading, error: searchResultsError } = useSearch();
  const mainSearchData = getSearchResults(SEARCH_SUGGESTIONS_INDEX_ID);
  const aiSearchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { aiSearchMetadata } = aiSearchData || {};
  const isAISummaryEnabled = aiSearchMetadata?.isEligibleForAiGeneratedSummary && isConsumerSearchPage;
  const { elements: mainSearchDataElements, query: providerQuery } = mainSearchData || {};
  const isLoadingOrHasErrors = isLoading || trendingItemsError || trendingProductsError || searchResultsError;

  const showNewAutocomplete = getShowNewAutocomplete();

  // Detect presence of degrees link in header to prevent width overflow
  React.useEffect(() => {
    if (!inServerContext && isUserLoggedIn) {
      const degreeLink = document.querySelector('[href="/degrees"]');
      setHasDegreeButton(!!degreeLink);
    }
  }, [isUserLoggedIn]);

  React.useEffect(() => {
    setIsDropdownOpen(false);
  }, [isMobileOrTabletView]);

  // On EQP and consumer search pages, the search bar is inline (always visible),
  // so we don't need to programmatically open the autocomplete dropdown.
  // On other mobile pages, clicking the search button opens a full-page
  // search overlay, and we need to manually open the CDS Autocomplete popover.
  // this also needs to be done for 1024-1060px on non-inline search bar pages
  const isNotInlineSearchBar = !isEqp && !isConsumerSearchPage;

  React.useEffect(() => {
    // searchIsOpen is only used for mobile search button click - opens full-page search overlay
    if (isNotInlineSearchBar && searchIsOpen) {
      const is1024To1060 =
        typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px) and (max-width: 1060px)').matches;
      if (isMobileOrTabletView || is1024To1060) {
        setIsDropdownOpen(true);
        if (rootInputRef.current) {
          rootInputRef.current.focus();
        }
      }
    }
    return undefined;
  }, [searchIsOpen, isNotInlineSearchBar, isMobileOrTabletView]);

  let showRetroStyles: boolean;
  if (showNewAutocomplete) {
    showRetroStyles = false;
  } else if (!isComposablePageHeader) {
    showRetroStyles = false;
  } else {
    showRetroStyles = !isAISummaryEnabled && isConsumerSearchPage && isMobileOrTabletView && !isDropdownOpen;
  }
  const is1061To1280 = useMediaQuery(`(min-width: ${breakpoints.values.sm}px) and (max-width: 1280px)`);
  const is1281To1440 = useMediaQuery('(min-width: 1281px) and (max-width: 1439px)');
  const is1440Plus = useMediaQuery('(min-width: 1440px)');

  // Calculate screen width based on breakpoints for createZeroStateAutocompleteItems
  const screenWidth = React.useMemo(() => {
    if (is1440Plus) return 1440;
    if (is1281To1440) return 1281;
    if (is1061To1280) return 1061;
    if (isMobileOrTabletView) return 1060;
    return undefined;
  }, [isMobileOrTabletView, is1061To1280, is1281To1440, is1440Plus]);

  // previous options - displayed during loading state
  const [prevOptions, setPrevOptions] = React.useState<AutocompleteItemGroup[]>([]);
  const autocompleteItemsInGroups = React.useMemo(() => {
    if (isLoadingOrHasErrors) return prevOptions;
    switch (autocompleteMode) {
      case AUTOCOMPLETE_MODES.AUTOCOMPLETE_ZERO_STATE:
        return createZeroStateAutocompleteItems(
          trendingSuggestions,
          isMobileOrTabletView,
          onboardingUrl,
          isUserLoggedIn,
          recentlySearchedItems,
          recentlyViewedItems,
          screenWidth,
          trendingProducts
        );
      case AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE:
        return createAsYouTypeAutocompleteItems(
          mainSearchDataElements,
          onboardingUrl,
          isUserLoggedIn,
          aiSearchData?.elements,
          searchText
        );
      default:
        return [];
    }
  }, [
    autocompleteMode,
    mainSearchDataElements,
    trendingSuggestions,
    trendingProducts,
    prevOptions,
    isLoadingOrHasErrors,
    recentlyViewedItems,
    recentlySearchedItems,
    isMobileOrTabletView,
    onboardingUrl,
    isUserLoggedIn,
    screenWidth,
    aiSearchData?.elements,
    searchText,
  ]);
  const trendingGroup = autocompleteItemsInGroups.find((g) => g.id === TRENDING_SEARCHES_GROUP_ID);
  const trendingTitle = trendingGroup?.title;

  // Programmatically open dropdown after hydration when autocomplete items are available
  // This fixes the SSR hydration issue where focus happens before CDS event listeners attach
  // Only runs once on initial mount - if user has existing search text (e.g., from back button), skip auto-open
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Don't auto-open if there's already search text (e.g., back button navigation)
    const hasExistingSearch = searchText && searchText.trim().length > 0;
    if (hasExistingSearch) return undefined;

    // Delay to ensure CDS Autocomplete is fully hydrated after SSR
    const HYDRATION_DELAY = 200;
    const BLUR_DELAY = 50;
    const RESET_DELAY = 300;

    const timers: NodeJS.Timeout[] = [];
    let isCancelled = false;

    const hydrationTimer = setTimeout(() => {
      if (isCancelled) return;

      const inputElement = rootInputRef.current || document.getElementById('search-autocomplete-input');

      if (inputElement instanceof HTMLElement) {
        const isAlreadyFocused = document.activeElement === inputElement;

        if (isAlreadyFocused) {
          // Input is already focused from SSR, need to blur then refocus to trigger CDS focus event
          isProgrammaticOpenRef.current = true;
          inputElement.blur();

          const refocusTimer = setTimeout(() => {
            if (isCancelled) return;
            if (inputElement && document.body.contains(inputElement)) {
              inputElement.focus();
            }

            const resetTimer = setTimeout(() => {
              if (isCancelled) return;
              isProgrammaticOpenRef.current = false;
            }, RESET_DELAY);

            timers.push(resetTimer);
          }, BLUR_DELAY);

          timers.push(refocusTimer);
        }
      }
    }, HYDRATION_DELAY);

    timers.push(hydrationTimer);

    return () => {
      isCancelled = true;
      timers.forEach((timer) => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refocusAndOpenDropdown = React.useCallback<(clearVirtualFocus?: boolean) => void>(
    (clearVirtualFocus = false) => {
      // Immediately focus the input to prevent focus loss and keep dropdown open
      if (rootInputRef.current) {
        rootInputRef.current.focus();
      }

      // Clear virtual focus if needed (for keyboard navigation)
      if (clearVirtualFocus) {
        setTimeout(() => {
          if (rootInputRef.current) {
            const ariaActiveDescendant = rootInputRef.current.getAttribute('aria-activedescendant');
            if (ariaActiveDescendant) {
              rootInputRef.current.removeAttribute('aria-activedescendant');
            }
          }

          // Reset the flag after a short delay
          setTimeout(() => {
            isRemovingItemRef.current = false;
          }, 100);
        }, 50);
      } else {
        // Reset the flag after a short delay to allow any pending selection events to be skipped
        setTimeout(() => {
          isRemovingItemRef.current = false;
        }, 100);
      }
    },
    []
  );

  const handleRemoveRecentSearch = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    setRefreshKey((prev) => prev + 1);
    onRefreshItems?.();
    refocusAndOpenDropdown(true);
  }, [onRefreshItems, refocusAndOpenDropdown]);

  const handleRemoveRecentlyViewed = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    setRefreshKey((prev) => prev + 1);
    onRefreshItems?.();
    refocusAndOpenDropdown();
  }, [onRefreshItems, refocusAndOpenDropdown]);

  const handleClearAllRecentSearches = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    clearAllRecentSearches();
    setRefreshKey((prev) => prev + 1);
    onRefreshItems?.();
    refocusAndOpenDropdown();
  }, [onRefreshItems, refocusAndOpenDropdown]);

  // Store updateQuery in ref to avoid recreating debounced function when it changes
  const updateQueryRef = React.useRef(updateQuery);
  React.useEffect(() => {
    updateQueryRef.current = updateQuery;
  }, [updateQuery]);

  // Create debounced function once - it uses the ref so it doesn't need to be recreated
  const debouncedRefineSearch = React.useMemo(
    () =>
      debounce((newValue: string) => {
        updateQueryRef.current(newValue);
      }, DEBOUNCE_DURATION_MS),
    []
  );

  // Cleanup: cancel pending debounced calls on unmount
  React.useEffect(() => {
    return () => {
      debouncedRefineSearch.cancel();
    };
  }, [debouncedRefineSearch]);

  // When the response is loading, we want to persist the previous options
  React.useEffect(() => {
    if (!isLoadingOrHasErrors && !isEqual(autocompleteItemsInGroups, prevOptions)) {
      setPrevOptions(autocompleteItemsInGroups);
    }
  }, [autocompleteItemsInGroups, prevOptions, isLoadingOrHasErrors]);

  // Update the query in the provider when the search text changes
  React.useEffect(() => {
    if (!isLoadingOrHasErrors && searchText && searchText !== providerQuery) {
      debouncedRefineSearch(searchText);
    }
  }, [searchText, debouncedRefineSearch, providerQuery, isLoadingOrHasErrors]);

  const handleSubmit = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (value: string, mergedItemsForTracking?: AutocompleteItems) => {
      onSubmit(decodeURIComponent(value), mergedItemsForTracking);
      // If we blur immediately, the text in the input will not update on selection
      setTimeout(() => {
        rootInputRef.current?.blur?.();
      });
    },
    [onSubmit]
  );

  // Determine the prefix icon based on styles and AI summary state
  let prefixIcon;
  if (showRetroStyles) {
    prefixIcon = (
      <IconButton
        intent="search"
        size="medium"
        css={[styles.searchButton, styles.hackyRetroButtonStyle]}
        type="submit"
      />
    );
  } else if (isAISummaryEnabled) {
    prefixIcon = <AIGenerateBrandedIcon aria-label={_t('AI Search')} />;
  }

  return (
    <div className="rc-SearchBarContent">
      {isDropdownOpen && (
        <div
          css={styles.backdrop}
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(false);
            hideMobileSearchPage?.();
          }}
          data-testid="search-autocomplete-backdrop"
          aria-hidden="true"
        />
      )}
      <Autocomplete
        popoverTrigger="focus"
        id="search-autocomplete"
        name="query"
        placeholder={isAISummaryEnabled ? _t('Ask Coursera AI or Search') : _t('What do you want to learn?')}
        items={autocompleteItemsInGroups}
        value={searchText}
        allowsCustomValue
        defaultValue={searchText}
        prefixIcon={prefixIcon}
        css={[
          styles.autocomplete(
            isComposablePageHeader,
            trendingTitle,
            isConsumerSearchPage,
            isEqp,
            hasDegreeButton,
            showNewAutocomplete
          ),
          showRetroStyles && styles.hackyRetroInputStyle,
        ]}
        inputActionButtons={
          !showRetroStyles && <IconButton intent="search" size="small" css={[styles.searchButton]} type="submit" />
        }
        onInputChange={(value) => {
          // Store previous search text before updating
          previousSearchTextRef.current = searchText;
          onInputChange(value);
        }}
        disableSelectionChangeOnTab
        onSelectionChange={(selectedOptionText) => {
          // Prevent the autocomplete from reopening after selection
          setIsDropdownOpen(false);

          // Skip selection if we just removed an item
          if (isRemovingItemRef.current) {
            return;
          }
          if (selectedOptionText) {
            const selectedText = String(selectedOptionText);
            // Find the matching item to get its ID and merged items for proper tracking
            let itemId: string | undefined;
            let mergedItemsForTracking: AutocompleteItems | undefined;
            for (const group of autocompleteItemsInGroups) {
              const matchingItem = group.children.find(
                (item) => item.textValue === selectedText || item.name === selectedText
              );
              if (matchingItem?.id) {
                itemId = matchingItem.id;
                // For RECENTLY_VIEWED or LEARNER_FAVORITES groups, use the merged children (includes trending products)
                // Also pass merged items for DIRECT_MATCHES items (trending products merged into these groups)
                if (
                  group.id === RECENTLY_VIEWED_GROUP_ID ||
                  group.id === LEARNER_FAVORITES_GROUP_ID ||
                  matchingItem.id.startsWith(AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES)
                ) {
                  mergedItemsForTracking = group.children;
                }
                break;
              }
            }
            // Pass the item ID if found, otherwise pass the text (for custom values)
            // Also pass merged items for tracking when available
            handleSubmit(encodeURIComponent(itemId || selectedText), mergedItemsForTracking);
          }
        }}
        inputProps={{
          id: 'search-autocomplete-input',
          'data-testid': 'search-autocomplete-input',
          name: 'query',
          type: 'text',
          onKeyUp: (e) => {
            if (e.key === 'Enter') {
              handleSubmit(encodeURIComponent(searchText));
            }
          },
        }}
        ref={rootInputRef}
        data-testid="search-autocomplete"
        aria-label={_t('Search catalog')}
        dropdownProps={{
          disablePortal: true,
          className: 'search-autocomplete-dropdown',
          style: {
            width: isComposablePageHeader ? '1000px' : '1000px',
            maxHeight: 'calc(100vh - 120px)',
          },
        }}
        drawerInputProps={{
          enterkeyhint: 'Search',
          onKeyUp: (e) => {
            if (e.key === 'Enter') {
              handleSubmit(encodeURIComponent(searchText));
            }
          },
        }}
        onClose={() => {
          // Don't close if we're programmatically opening the dropdown (during blur/focus cycle)
          if (isProgrammaticOpenRef.current) {
            return;
          }

          // Don't close if focus is moving to a button inside the dropdown
          // This allows keyboard navigation to the X button via Right Arrow or clicking "Clear all"
          setTimeout(() => {
            const activeElement = document.activeElement;
            const isButtonInDropdown =
              activeElement &&
              (activeElement.getAttribute('data-testid') === 'clear-recent-search' ||
                activeElement.classList.contains('clear-all-recent-searches-button')) &&
              activeElement.closest('.search-autocomplete-dropdown');

            if (!isButtonInDropdown) {
              setIsDropdownOpen(false);
              hideMobileSearchPage?.();
            }
          }, 0);
        }}
        onOpen={() => {
          setIsDropdownOpen(true);
        }}
      >
        {({ title: groupTitle, children, id: groupId }) => {
          if (groupId === TRENDING_SEARCHES_GROUP_ID) {
            return (
              <Autocomplete.Group key={groupId} items={children} title={groupTitle} aria-label={groupTitle}>
                {children.map(({ id, supportText, name, textValue }, idx) => (
                  <Autocomplete.Option
                    key={name}
                    supportText={supportText}
                    textValue={textValue ?? name}
                    aria-label={name}
                  >
                    <AutocompleteTrendingOption
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                    >
                      {name}
                    </AutocompleteTrendingOption>
                  </Autocomplete.Option>
                ))}
              </Autocomplete.Group>
            );
          }

          if (groupId === ONBOARDING_GROUP_ID) {
            const onboardingItem = children[0];
            if (!onboardingItem) return null;

            return (
              <Autocomplete.Group key={groupId} items={children} title="" aria-label={_t('Onboarding')}>
                <Autocomplete.Option
                  key={onboardingItem.id}
                  textValue={onboardingItem.textValue ?? onboardingItem.name}
                  aria-label={onboardingItem.name}
                >
                  <AutocompleteOnboardingOption
                    autocompleteState={autocompleteMode}
                    id={onboardingItem.id}
                    recentlyViewedItems={recentlyViewedItems}
                    suggestionIndex={0}
                    path={onboardingItem.path}
                  />
                </Autocomplete.Option>
              </Autocomplete.Group>
            );
          }

          if (groupId === RECENTLY_VIEWED_GROUP_ID || groupId === LEARNER_FAVORITES_GROUP_ID) {
            const productCards = children
              .map((item, idx) => {
                return (
                  <Autocomplete.Option key={item.name} textValue={item.name} aria-label={item.name}>
                    <AutocompleteSearchProductCard
                      autocompleteState={autocompleteMode}
                      id={item.id || ''}
                      recentlyViewedItems={children}
                      suggestionIndex={idx}
                      item={item}
                      groupId={groupId}
                      onRemove={handleRemoveRecentlyViewed}
                    />
                  </Autocomplete.Option>
                );
              })
              .filter((card): card is React.ReactElement => card !== null);

            return (
              <Autocomplete.Group
                key={
                  groupId === RECENTLY_VIEWED_GROUP_ID || groupId === LEARNER_FAVORITES_GROUP_ID
                    ? `${groupId}-${refreshKey}`
                    : groupId
                }
                items={children}
                title={groupTitle}
                aria-label={groupTitle}
              >
                {productCards}
              </Autocomplete.Group>
            );
          }

          return (
            <Autocomplete.Group
              key={groupId === RECENT_SEARCHES_GROUP_ID ? `${groupId}-${refreshKey}` : groupId}
              items={children}
              // @ts-expect-error: Autocomplete.Group title prop expects string, but we need to render a button alongside the title
              title={
                groupId === RECENT_SEARCHES_GROUP_ID ? (
                  <div
                    css={css`
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      width: 100%;
                    `}
                  >
                    <span>{groupTitle}</span>
                    <Button
                      variant="ghostSecondary"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                        handleClearAllRecentSearches();
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          e.nativeEvent.stopImmediatePropagation();
                          handleClearAllRecentSearches();
                        }
                      }}
                      onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      onMouseUp={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      onPointerDown={(e: React.PointerEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        e.nativeEvent.stopImmediatePropagation();
                      }}
                      tabIndex={0}
                      className="clear-all-recent-searches-button"
                      aria-label={_t('Clear all recent searches')}
                    >
                      {_t('Clear all')}
                    </Button>
                  </div>
                ) : (
                  groupTitle
                )
              }
            >
              {children.map(({ imageUrl, id, supportText, name, textValue }, idx) => (
                <Autocomplete.Option
                  prefix={<AutocompleteItemIcon imageUrl={imageUrl} groupId={groupId} avatarAltText={name} />}
                  key={name}
                  supportText={supportText}
                  textValue={textValue ?? name}
                  aria-label={name}
                >
                  {groupId === RECENT_SEARCHES_GROUP_ID ? (
                    <AutocompleteRecentlySearchedOption
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                      onRemove={handleRemoveRecentSearch}
                      inputRef={rootInputRef}
                    >
                      {name}
                    </AutocompleteRecentlySearchedOption>
                  ) : (
                    <AutocompleteOptionTrackingDiv
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                      searchText={providerQuery}
                    >
                      {name}
                    </AutocompleteOptionTrackingDiv>
                  )}
                </Autocomplete.Option>
              ))}
            </Autocomplete.Group>
          );
        }}
      </Autocomplete>
    </div>
  );
};

export default SearchBarContent;
